import { datadogRum } from "@datadog/browser-rum";
// @ts-expect-error - `cookie-cutter` has no exported types
import Cookie from "cookie-cutter";
import Script from "next/script";
import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import aa from "search-insights";

import { useAuthContext } from "~/contexts/auth";
import useSubscriptions from "~/hooks/use-subscriptions";
import CommonUtils from "~/utils/common-utils";

type VendorConsents = Record<string, boolean>;
type PurposesConsents = Record<string, boolean>;

type ContextProps = PropsWithChildren;
type ContextAttrs = {
  vendorConsents: VendorConsents;
  purposesConsents: PurposesConsents;
  updateVendor: (id: string, value: boolean) => void;
  updatePurpose: (id: string, value: boolean) => void;
};

const Context = createContext<ContextAttrs>({
  vendorConsents: {},
  purposesConsents: {},
  updateVendor: () => {},
  updatePurpose: () => {},
});

export default function UserConsentsProvider(props: Readonly<ContextProps>) {
  const { user, isAuthenticated } = useAuthContext();
  const [vendorConsents, setVendorConsents] = useState<VendorConsents>({});
  const [purposesConsents, setPurposesConsents] = useState<PurposesConsents>({});
  const { getUserSubscriptionValueByBackendId } = useSubscriptions();

  const personalizationProfiling = getUserSubscriptionValueByBackendId("ProfilingOptIn");

  // effect to enable/disable algolia trackings via cookie
  useEffect(() => {
    datadogRum.setTrackingConsent(purposesConsents?.analytics === true ? "granted" : "not-granted");
  }, [purposesConsents]);

  useEffect(() => {
    if (user && isAuthenticated && personalizationProfiling) {
      aa("setAuthenticatedUserToken", `${user.ID}`);
      Cookie.set("algolia-user-token", `${user.ID}`, {
        path: "/",
      });
    } else {
      aa("setAuthenticatedUserToken", undefined);
      Cookie.set("algolia-user-token", "", {
        path: "/",
      });
    }
  }, [user, isAuthenticated, personalizationProfiling]);

  const updateVendor = (id: string, value: boolean) => {
    setVendorConsents((oldConsents) => {
      const newConsents = { ...oldConsents };
      newConsents[id] = value;
      return newConsents;
    });
  };

  const updatePurpose = (id: string, value: boolean) => {
    setPurposesConsents((oldConsents) => {
      const newConsents = { ...oldConsents };
      newConsents[id] = value;
      return newConsents;
    });
  };

  return (
    <Context.Provider
      value={{
        vendorConsents,
        purposesConsents,
        updateVendor,
        updatePurpose,
      }}
    >
      {props.children}
      {/* CDP Personalization */}
      {CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_EVERGAGE_SCRIPT) && purposesConsents["profiling"] && (
        <Script src={process.env.NEXT_PUBLIC_EVERGAGE_SCRIPT} />
      )}
    </Context.Provider>
  );
}

export function useConsents() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(`useConsents must be used within a UserConsentsProvider`);
  }

  return context;
}
